import { type DescMethod } from '@bufbuild/protobuf';
import { AuthService } from '@qdrant/qdrant-cloud-public-api/auth/v1/auth';
import { OAuthService } from '@qdrant/qdrant-cloud-public-api/auth/v1/oauth';
import { IAMService } from '@qdrant/qdrant-cloud-public-api/iam/v1';
import { type LinkProps } from '@tanstack/react-router';
import { checkRpcPermissions } from '@/api/permissions';
import { type NonEmptyArray } from '@/utils/types';

/**
 * What a tab calls itself: a fixed name, or — for a tab that covers more ground for some accounts than
 * others — the name it works out from what the account may read.
 */
type TabLabel = string | ((permissions: ReadonlySet<string>) => string);

type AccessTabConfig = {
  value: 'roles' | 'cloudManagementKeys' | 'connectedApps';
  label: TabLabel;
  /** Where the tab links to, and what marks it active — prefix-matched over the whole subtree. */
  to: NonNullable<LinkProps['to']>;
  /** The leaf the index route lands on, when `to` is a layout with no screen of its own. */
  entrypoint?: NonNullable<LinkProps['to']>;
  /** The RPC the tab's content is built on — being allowed to call it is what makes it reachable. */
  method: DescMethod;
  /** Shown only while the `oauth` feature gate is on. */
  oauthOnly?: true;
};

/** The feature gates a tab can sit behind, as resolved for the current user. */
export type AccessTabGates = { oauth: boolean };

/** A readable tab, under the single name the account's permissions earned it. */
export type AccessTab = Omit<AccessTabConfig, 'label'> & { label: string };

/**
 * The tabs of the access-management subtree, in the order the index route falls through them when it
 * picks an entrypoint. One definition for both the tab bar and that redirect, so a tab can never be
 * offered — or redirected to — while the account may not read it.
 */
const ACCESS_TABS: AccessTabConfig[] = [
  {
    value: 'roles',
    // The users tab doubles as the role editor for whoever may also read roles, and says so.
    label: (permissions) =>
      checkRpcPermissions([IAMService.method.listRoles], permissions).matches
        ? 'User & Role Management'
        : 'User Management',
    to: '/accounts/$accountId/cloud-access/roles',
    entrypoint: '/accounts/$accountId/cloud-access/roles/all-users',
    method: IAMService.method.listUsers,
  },
  {
    value: 'cloudManagementKeys',
    label: 'Cloud Management Keys',
    to: '/accounts/$accountId/cloud-access/cloud-management-keys',
    method: AuthService.method.listManagementKeys,
  },
  {
    value: 'connectedApps',
    label: 'Connected Apps',
    to: '/accounts/$accountId/cloud-access/connected-apps',
    method: OAuthService.method.listAccountOAuthGrants,
    oauthOnly: true,
  },
];

const enabledTabs = (gates: AccessTabGates) => ACCESS_TABS.filter(({ oauthOnly }) => !oauthOnly || gates.oauth);

/**
 * What is still missing to reach any tab at all — the union of each tab's own unmet requirements, so a
 * tab whose requirement is already half-held does not report the half that is.
 */
export function missingTabPermissions(permissions: ReadonlySet<string>, gates: AccessTabGates): string[] {
  const methods = enabledTabs(gates).map(({ method }) => method) as NonEmptyArray<DescMethod>;

  return checkRpcPermissions(methods, permissions, 'anyOf').missingPermissions;
}

/**
 * The tabs this account may read, in tab order, each under the name its permissions earn it. Empty
 * means the whole subtree is out of reach.
 */
export function readableTabs(permissions: ReadonlySet<string>, gates: AccessTabGates): AccessTab[] {
  const readable: AccessTab[] = [];

  // Each tab is rebuilt rather than handed back: `ACCESS_TABS` is module state, and resolving a name
  // into it would outlive this call and answer the next one with a name its permissions did not earn.
  for (const { label, ...tab } of enabledTabs(gates)) {
    if (checkRpcPermissions([tab.method], permissions).matches) {
      readable.push({ ...tab, label: typeof label === 'string' ? label : label(permissions) });
    }
  }

  return readable;
}
